import { useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import APIService from 'api/APIService'
import { useSettings } from 'core/hooks/settings/useSettings'
import useAuth from 'core/hooks/auth/useAuth'
import useNotifications from 'core/hooks/ui/useNotifications'
import { StartUrl } from 'constants/RouteUrls'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { isRefreshTokenRejected } from 'core/hooks/auth/motusSession'

const REFRESH_INTERVAL = 14 * 60 * 1000 // 14 minutes in milliseconds

/**
 * Custom hook that manages automatic refresh of Motus access tokens.
 *
 * This hook handles the automatic renewal of access tokens every 14 minutes (before the 15-minute server expiration).*
 * @remarks
 * - The refresh token is expected to live forever
 * - The access token expires every 15 minutes on the server
 * - Automatically refreshes the access token every 14 minutes when a refresh token is present
 * - If a refresh token exists but no access token, immediately fetches a new access token
 * - Should be implemented at the root of the component tree
 */
const useRefreshToken = () => {
  const {
    data: {
      auth: { refreshToken, accessToken }
    }
  } = useSettings()

  const { setMotusTokens } = useAuth()
  const { replace } = useNavigation()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastRefreshRef = useRef(Date.now())

  const notifications = useNotifications()
  // Mutation to refresh the access token
  const { mutate: refreshAccessToken } = useMutation({
    mutationFn: async () => {
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      return await APIService.motus.getAccessToken({
        refreshToken
      })
    },
    onSuccess: (newAccessToken: string) => {
      lastRefreshRef.current = Date.now()
      // Update local state to reflect the new token
      setMotusTokens({
        accessToken: newAccessToken,
        refreshToken: null
      })
    },
    onError: (error) => {
      // Network errors, 5xx, 429 and timeouts are transient: keep the session and
      // let the next interval/visibility refresh retry.
      if (!isRefreshTokenRejected(error)) return

      // Clear auth state too, otherwise the still-authenticated user is bounced
      // from the login page straight back to the dashboard.
      setMotusTokens({ accessToken: null, refreshToken: null })
      notifications.error({
        title: 'Session Expired',
        subtitle: 'Your session has expired. Please log in again.',
        id: 'session-expired'
      })
      replace(StartUrl)
    }
  })

  // A new access token (login or refresh) restarts the refresh window
  useEffect(() => {
    lastRefreshRef.current = Date.now()
  }, [accessToken])

  // Set up automatic token refresh interval
  useEffect(() => {
    // Only set up the interval if we have a refresh token
    if (!refreshToken) {
      return
    }

    // Refresh immediately on mount if we have a refresh token but no access token
    if (!accessToken) {
      refreshAccessToken()
    }

    // Set up interval to refresh every 14 minutes
    intervalRef.current = setInterval(() => {
      refreshAccessToken()
    }, REFRESH_INTERVAL)

    // Timers are throttled in background tabs, so refresh when the tab wakes up
    const onVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        Date.now() - lastRefreshRef.current >= REFRESH_INTERVAL
      ) {
        refreshAccessToken()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // Cleanup interval on unmount
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [refreshToken, accessToken, refreshAccessToken])

  return null
}

export default useRefreshToken
